/**
 * useUserSets — Gestiona el pool P2P del usuario (añadir/eliminar sets).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export type MinifigCondition = "complete" | "alternative_minifigs" | "no_minifigs";

export const MINIFIG_CONDITION_LABEL: Record<MinifigCondition, string> = {
  complete: "Completo (100% piezas y figuras originales)",
  alternative_minifigs: "Minifiguras alternativas (100% piezas)",
  no_minifigs: "Sin minifiguras (100% piezas)",
};

export const MINIFIG_CONDITION_SHORT_LABEL: Record<MinifigCondition, string> = {
  complete: "Completo",
  alternative_minifigs: "Minifiguras alternativas",
  no_minifigs: "Sin minifiguras",
};

export interface UserSet {
  id: string;
  user_id: string;
  set_id: string;
  set_ref: string;
  status: "available" | "borrowed" | "returning" | "maintenance" | "retired";
  minifig_condition?: MinifigCondition;
  condition_notes: string | null;
  daily_rate_cents?: number | null;
  weekly_rate_cents?: number | null;
  custom_deposit_cents?: number | null;
  requires_deposit?: boolean;
  market_value_eur?: number | null;
  created_at: string;
  sets?: {
    set_name: string;
    set_image_url: string;
    set_piece_count: number;
    set_price: number;
    set_theme: string;
  } | null;
}

export const USERSET_STATUS_LABEL: Record<UserSet["status"], string> = {
  available: "Disponible",
  borrowed: "Prestado",
  returning: "Devolviéndose",
  maintenance: "En revisión",
  retired: "Retirado",
};

export function useUserSets() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user_sets", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<UserSet[]> => {
      const { data, error } = await supabase
        .from("user_sets")
        .select(`
          *,
          sets:set_id ( set_name, set_image_url, set_piece_count, set_price, set_theme )
        `)
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      const rawUserSets = (data ?? []) as unknown as UserSet[];

      // Enriquecer de forma opcional con demand_2004 (BL_sales_avg_price_new, top3 o used)
      if (rawUserSets.length > 0) {
        const refsToQuery = Array.from(
          new Set(
            rawUserSets.flatMap((s) => {
              if (!s.set_ref) return [];
              const base = s.set_ref.trim().replace(/-1$/, "");
              return [base, `${base}-1`];
            })
          )
        );
        try {
          const { data: demandRows } = await supabase
            .from("demand_2004")
            .select("set_ref, BL_sales_avg_price_new, BL_sales_top3_cheapest_avg_new, BL_sales_avg_price_used")
            .in("set_ref", refsToQuery);

          if (demandRows && demandRows.length > 0) {
            const demandMap = new Map<string, number>();
            for (const row of demandRows) {
              const blPrice = Number(
                row.BL_sales_avg_price_new ||
                row.BL_sales_top3_cheapest_avg_new ||
                row.BL_sales_avg_price_used ||
                0
              );
              if (blPrice > 0) {
                const base = row.set_ref.replace(/-1$/, "");
                demandMap.set(base, blPrice);
                demandMap.set(row.set_ref, blPrice);
              }
            }
            return rawUserSets.map((us) => {
              const base = us.set_ref ? us.set_ref.trim().replace(/-1$/, "") : "";
              return {
                ...us,
                market_value_eur:
                  demandMap.get(base) ??
                  demandMap.get(us.set_ref) ??
                  us.sets?.set_price ??
                  null,
              };
            });
          }
        } catch {
          // Si falla la consulta a demand_2004, continuar con sets.set_price
        }
      }

      return rawUserSets;
    },
  });
}

export interface UpdateSetPricingPayload {
  userSetId: string;
  dailyRateCents: number | null;
  weeklyRateCents: number | null;
  requiresDeposit: boolean;
  customDepositCents: number | null;
}

export function useUpdateSetPricing() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async ({
    userSetId,
    dailyRateCents,
    weeklyRateCents,
    requiresDeposit,
    customDepositCents,
  }: UpdateSetPricingPayload) => {
    const { error } = await supabase
      .from("user_sets")
      .update({
        daily_rate_cents: dailyRateCents,
        weekly_rate_cents: weeklyRateCents,
        requires_deposit: requiresDeposit,
        custom_deposit_cents: requiresDeposit ? customDepositCents : null,
      })
      .eq("id", userSetId);

    if (error) {
      toast.error("Error al actualizar tarifas: " + error.message);
      return false;
    }

    toast.success("Condiciones de alquiler actualizadas");
    queryClient.invalidateQueries({ queryKey: ["user_sets", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["community_pool"] });
    return true;
  };
}

export function useAddToPool() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (setRef: string, minifigCondition: MinifigCondition = "complete") => {
    const { data, error } = await supabase.functions.invoke("add-to-pool", {
      body: { set_ref: setRef, minifig_condition: minifigCondition },
    });
    if (error || data?.success === false) {
      toast.error(data?.error ?? error?.message ?? "Error");
      return null;
    }
    toast.success(`${data.data.set_name} añadido a tu pool`);
    queryClient.invalidateQueries({ queryKey: ["user_sets", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["community_pool"] });
    return data.data;
  };
}

export function useRemoveFromPool() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (userSetId: string) => {
    const { data, error } = await supabase.functions.invoke("remove-from-pool", {
      body: { user_set_id: userSetId },
    });
    if (error || data?.success === false) {
      toast.error(data?.error ?? error?.message ?? "Error");
      return false;
    }
    toast.success("Set retirado de tu pool");
    queryClient.invalidateQueries({ queryKey: ["user_sets", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["community_pool"] });
    return true;
  };
}

export function useUpdateMinifigCondition() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (userSetId: string, condition: MinifigCondition) => {
    const { error } = await supabase
      .from("user_sets")
      .update({ minifig_condition: condition })
      .eq("id", userSetId);

    if (error) {
      toast.error("Error al actualizar estado del set: " + error.message);
      return false;
    }
    toast.success(`Estado actualizado a: ${MINIFIG_CONDITION_SHORT_LABEL[condition]}`);
    queryClient.invalidateQueries({ queryKey: ["user_sets", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["community_pool"] });
    return true;
  };
}